import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Truck, Check } from "lucide-react";
import empApi from "../../lib/empApi";

export default function EmpNotificationBell() {
  const navigate = useNavigate();
  const [data, setData] = useState({ notifications: [], unread: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await empApi.get("/employee/notifications"); setData(data); }
    catch (e) { /* ignore */ }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const openItem = async (n) => {
    try { await empApi.post(`/employee/notifications/${n.id}/read`); } catch (e) { /* ignore */ }
    setOpen(false);
    if ((n.link || "").startsWith("/karyawan")) navigate(n.link);
    load();
  };

  const markAll = async () => {
    try { await empApi.post("/employee/notifications/read-all"); } catch (e) { /* ignore */ }
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button onClick={() => setOpen((o) => !o)} data-testid="emp-notif-bell"
        className="relative rounded-lg p-2 text-slate-300 hover:bg-slate-800 hover:text-white">
        <Bell size={18} />
        {data.unread > 0 && (
          <span data-testid="emp-notif-badge"
            className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-400 px-1 text-[10px] font-bold text-slate-900">
            {data.unread}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="emp-notif-panel"
          className="absolute right-0 top-11 z-30 w-72 overflow-hidden rounded-2xl border border-slate-800 bg-slate-900 shadow-2xl">
          <div className="flex items-center justify-between border-b border-slate-800 px-3 py-2.5">
            <p className="text-sm font-bold text-white">Notifikasi</p>
            {data.unread > 0 && (
              <button onClick={markAll} data-testid="emp-notif-readall" className="flex items-center gap-1 text-[11px] font-semibold text-amber-400">
                <Check size={12} /> Tandai dibaca
              </button>
            )}
          </div>
          <div className="max-h-80 overflow-y-auto">
            {data.notifications.length === 0 ? (
              <p className="px-4 py-6 text-center text-xs text-slate-500" data-testid="emp-notif-empty">Belum ada notifikasi.</p>
            ) : (
              data.notifications.map((n) => (
                <button key={n.id} onClick={() => openItem(n)} data-testid={`emp-notif-${n.id}`}
                  className={`flex w-full items-start gap-2.5 border-b border-slate-800/70 px-3 py-2.5 text-left transition hover:bg-slate-800/60 ${n.read ? "opacity-60" : ""}`}>
                  <span className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-amber-400/15 text-amber-300"><Truck size={14} /></span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold text-white">{n.title}</p>
                    <p className="text-xs text-slate-400">{n.body}</p>
                    <p className="mt-0.5 text-[10px] text-slate-600">{(n.created_at || "").slice(0, 16).replace("T", " ")}</p>
                  </div>
                  {!n.read && <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-amber-400" />}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
